import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import moment from 'moment';
import { CalendarData, Calendar_onValueChangeEvent, Dates } from '@inmoment/the-kitchen';

import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { WidgetDataSource } from '../../../../models';
import { TimezoneService } from '../../../../services/timezone.service';

export interface PredictiveDateRange {
  previous: Dates;
  current: Dates;
}

export interface PredictiveDateRangeString {
  previous: { first: string; last: string };
  current: { first: string; last: string };
}

@EnableClassTelemetry()
@Component({
  selector: 'app-predictive-widget-datepickers',
  templateUrl: './predictive-widget-datepickers.component.html',
  styleUrls: ['./predictive-widget-datepickers.component.scss'],
  standalone: false,
})
export class PredictiveWidgetDatepickersComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() dashboard: Dashboard;
  @Input() widgetDataSource: WidgetDataSource;
  @Input() widget: Widget;

  @Output() predictiveDateRangeChanged = new EventEmitter<PredictiveDateRange>();

  public predictiveWidgetDateCalendarCurrent: CalendarData;
  public predictiveWidgetDateCalendarTrend: CalendarData;
  public validationFailed = false;
  public validationTooltip = '';
  public predictiveDateRange: PredictiveDateRange = {
    previous: { first: null, last: null },
    current: { first: null, last: null },
  };
  public predictiveDateRangeString: PredictiveDateRangeString;
  public prevDateMin: Date;
  public prevDateMax: Date;
  public currDateMin: Date;
  public currDateMax: Date;

  constructor(private timezoneService: TimezoneService) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widgetDataSource?.currentValue) {
      if (!changes.widgetDataSource?.firstChange) {
        delete this.widget.payload.predictiveDateRange;
      }
      this.setDateRanges();
    }
  }

  onPredictiveWidgetDateChange($event: any, version: 'trend' | 'current') {
    const calData: Calendar_onValueChangeEvent = $event.detail;
    setTimeout(() => {
      const dateRange: Dates = calData.dates;
      const timezone = this.timezoneService.timezone;
      const firstDate = moment(dateRange.first).startOf('day').tz(timezone, true).toDate();
      const lastDate = moment(dateRange.last).endOf('day').tz(timezone, true).toDate();
      if (version === 'trend') {
        this.predictiveDateRange.previous.first = firstDate;
        this.predictiveDateRange.previous.last = lastDate;
      } else {
        this.predictiveDateRange.current.first = firstDate;
        this.predictiveDateRange.current.last = lastDate;
      }
      this.validateAndEmitDateRange();
    });
  }

  private validateAndEmitDateRange() {
    const lastPrevDate = this.predictiveDateRange.previous.last;
    const firstPrevDate = this.predictiveDateRange.previous.first;
    const firstCurrDate = this.predictiveDateRange.current.first;
    const lastCurrDate = this.predictiveDateRange.current.last;

    lastPrevDate.setHours(0, 0, 0, 0);
    firstPrevDate.setHours(0, 0, 0, 0);
    firstCurrDate.setHours(0, 0, 0, 0);
    lastCurrDate.setHours(0, 0, 0, 0);

    if (firstCurrDate <= lastPrevDate) {
      this.validationFailed = true;
      this.validationTooltip = translate('widgets.predictive.previous-must-be-earlier');
    } else if (firstPrevDate < this.prevDateMin) {
      this.validationFailed = true;
      this.validationTooltip = translate('widgets.predictive.previous-is-outside');
    } else if (lastCurrDate > this.currDateMax) {
      this.validationFailed = true;
      this.validationTooltip = translate('widgets.predictive.current-is-outside');
    } else {
      this.validationFailed = false;
      this.validationTooltip = '';
      //convert dates to strings for payload
      this.predictiveDateRangeString = {
        previous: { first: firstPrevDate.toISOString(), last: lastPrevDate.toISOString() },
        current: { first: firstCurrDate.toISOString(), last: lastCurrDate.toISOString() },
      };
      this.widget.payload.predictiveDateRange = { ...this.predictiveDateRangeString };

      this.predictiveDateRangeChanged.emit({ ...this.predictiveDateRange });
    }
  }

  private setDateRanges() {
    //constrain calendar pickers to within data range
    this.prevDateMin = this.widgetDataSource.minDocAt;
    this.prevDateMax = this.widgetDataSource.maxDocAt;
    this.currDateMin = this.widgetDataSource.minDocAt;
    this.currDateMax = this.widgetDataSource.maxDocAt;

    this.prevDateMin.setHours(0, 0, 0, 0);
    this.prevDateMax.setHours(0, 0, 0, 0);
    this.currDateMin.setHours(0, 0, 0, 0);
    this.currDateMax.setHours(0, 0, 0, 0);
    let initTrendStartDate: Date;
    let initTrendEndDate: Date;
    let initCurrentStartDate: Date;
    let initCurrentEndDate: Date;

    const timezone = this.timezoneService.timezone;
    //Set initial dates if not previously saved
    if (!this.widget.payload.predictiveDateRange) {
      initTrendStartDate = moment(this.widgetDataSource.maxDocAt)
        .subtract(1, 'month')
        .startOf('month')
        .tz(timezone, true)
        .toDate();
      initTrendEndDate = moment(this.widgetDataSource.maxDocAt)
        .subtract(1, 'month')
        .endOf('month')
        .tz(timezone, true)
        .toDate();

      initCurrentEndDate = moment(this.widgetDataSource.maxDocAt)
        .endOf('day')
        .tz(timezone, true)
        .toDate();
      initCurrentStartDate = moment(this.widgetDataSource.maxDocAt)
        .startOf('month')
        .tz(timezone, true)
        .toDate();
      //Set saved dates from payload if available
    } else {
      initTrendStartDate = new Date(this.widget.payload.predictiveDateRange.previous.first);
      initTrendEndDate = new Date(this.widget.payload.predictiveDateRange.previous.last);
      initCurrentStartDate = new Date(this.widget.payload.predictiveDateRange.current.first);
      initCurrentEndDate = new Date(this.widget.payload.predictiveDateRange.current.last);
    }
    //Dates must be cloned to pass into Calendar Picker or picker will change time in Dashboard (DASH-5652)
    const pickerTrendStartDate = new Date(initTrendStartDate.getTime());
    const pickerTrendEndDate = new Date(initTrendEndDate.getTime());
    const pickerCurrentStartDate = new Date(initCurrentStartDate.getTime());
    const pickerCurrendEndDate = new Date(initCurrentEndDate.getTime());

    //Pass dates to calendar picker
    this.predictiveWidgetDateCalendarTrend = {
      initialDateRange: {
        first: pickerTrendStartDate,
        last: pickerTrendEndDate,
      },
    };

    this.predictiveWidgetDateCalendarCurrent = {
      initialDateRange: {
        first: pickerCurrentStartDate,
        last: pickerCurrendEndDate,
      },
    };

    this.predictiveDateRange = {
      previous: { first: initTrendStartDate, last: initTrendEndDate },
      current: { first: initCurrentStartDate, last: initCurrentEndDate },
    };
    this.predictiveDateRangeChanged.emit(this.predictiveDateRange);
  }
}
